import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight, Star, Cpu, Zap, Layout, Code, ChevronRight, ChevronLeft } from 'lucide-react';
import axiosInstance, { BACKEND_ROOT_URL } from '../../utils/axiosInstance';
import ProductCard from '../components/ProductCard';
import { Link, useNavigate } from 'react-router-dom';
import Animation3D from '../components/Animation3D';

// --- THEME PALETTE ---
const COLORS = {
  bg: "#020617",         // Deep Space
  panel: "#0f172a",      // Slate 900
  accent: "#22d3ee",     // Electric Cyan
  textMain: "#94a3b8",   // Slate 400
  textLight: "#f1f5f9",  // Slate 100
};

const TemplateStore = () => {
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axiosInstance.get(`/products`);
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products', error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get('/categories');
        setCategories(response.data.sort((a, b) => a.sequence - b.sequence));
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  // --- SLIDER LOGIC ---
  const scroll = (direction) => {
    const { current } = scrollRef;
    if (current) {
        // Scroll by the width of the container (one full view)
        const scrollAmount = direction === 'left' ? -current.offsetWidth : current.offsetWidth;
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] font-sans text-slate-300 relative overflow-hidden">
      
      {/* --- BACKGROUND DECORATION --- */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(34,211,238,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(34,211,238,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

      {/* --- HERO SECTION --- */}
      <div className="relative z-10">
        <Animation3D />
      </div>

      {/* --- TECH STATS BAR --- */}
      <div className="relative z-20 border-y border-[#22d3ee]/20 bg-[#0f172a]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#22d3ee]/10">
          {[
            { text: 'Fully Responsive', icon: <Layout className="w-4 h-4" /> },
            { text: 'Modern Frameworks', icon: <Cpu className="w-4 h-4" /> },
            { text: 'Easy Config', icon: <Zap className="w-4 h-4" /> },
            { text: 'Clean Code', icon: <Code className="w-4 h-4" /> }
          ].map((prop, idx) => (
            <div key={idx} className="py-6 flex flex-col items-center justify-center gap-2 group cursor-default hover:bg-[#22d3ee]/5 transition-colors">
              <div className="text-[#22d3ee] group-hover:scale-110 group-hover:drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] transition-all duration-300">
                {prop.icon}
              </div>
              <span className="text-[10px] md:text-xs uppercase tracking-[0.15em] font-bold text-slate-400 group-hover:text-white transition-colors">
                {prop.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* --- CATEGORIES SLIDER MODULE --- */}
      <section className="relative max-w-[1400px] mx-auto px-4 py-24 z-10">
        
        {/* Section Header */}
        <div className="flex justify-between items-end mb-8 px-2">
            <div>
                <div className="inline-flex items-center gap-2 mb-2">
                    <span className="text-[#22d3ee] font-mono text-xs tracking-widest uppercase">// SYSTEM_MODULES</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                    Browse by <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#22d3ee] to-blue-500">Industry</span>
                </h2>
            </div>
            
            {/* Slider Controls */}
            <div className="flex gap-2">
                <button onClick={() => scroll('left')} className="p-2 border border-[#22d3ee]/30 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-[#020617] transition-all rounded-sm backdrop-blur-sm">
                    <ChevronLeft size={20} />
                </button>
                <button onClick={() => scroll('right')} className="p-2 border border-[#22d3ee]/30 text-[#22d3ee] hover:bg-[#22d3ee] hover:text-[#020617] transition-all rounded-sm backdrop-blur-sm">
                    <ChevronRight size={20} />
                </button>
            </div>
        </div>
        
        {/* SLIDER CONTAINER */}
        <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory py-4"
        >
          {categories.map((cat) => (
            <div 
                key={cat.name} 
                className="group relative flex-shrink-0 snap-center cursor-pointer overflow-hidden border border-[#22d3ee]/20 bg-[#0f172a]
                           w-[85%]       /* Mobile: 1 item fully visible */
                           sm:w-[45%]    /* Tablet: 2 items */
                           md:w-[30%]    /* Small Laptop: 3 items */
                           lg:w-[19%]    /* Desktop: 5 items (Approx 20% minus gap) */
                           h-[320px]"    /* Reduced Height */
            >
              
              {/* Image with Tech Filter */}
              <div className="absolute inset-0 bg-[#0f172a]">
                 <img 
                  src={BACKEND_ROOT_URL + '/' + cat.image} 
                  alt={cat.name} 
                  className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 ease-out"
                />
              </div>

              {/* Scanline Effect */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-0 bg-[length:100%_2px,3px_100%] pointer-events-none"></div>
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/40 to-transparent opacity-90 group-hover:opacity-60 transition-opacity duration-500"></div>

              {/* Corner Decorations */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#22d3ee] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#22d3ee] opacity-0 group-hover:opacity-100 transition-opacity"></div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="text-xl font-bold text-white mb-1 tracking-wide font-sans group-hover:text-[#22d3ee] transition-colors shadow-black drop-shadow-md">{cat.name}</h3>
                
                <button 
                    onClick={() => navigate(`/products?category=${cat.id}`)} 
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400 group-hover:text-white transition-colors mt-2"
                >
                  View <ChevronRight className="w-3 h-3 text-[#22d3ee]" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- POPULAR TEMPLATES --- */}
      <section className="max-w-7xl mx-auto px-4 py-20 border-t border-[#22d3ee]/10 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white mb-1">
                Recent <span className="text-[#22d3ee]">Template</span>
            </h2>
            <p className="text-xs text-slate-500 font-mono">// RECENT</p>
          </div>
          
          <Link to={'/products'} className="group flex items-center text-xs uppercase tracking-widest font-bold text-slate-400 hover:text-[#22d3ee] transition-colors border border-slate-800 hover:border-[#22d3ee] px-4 py-2 rounded-sm bg-[#0f172a]">
            View All Data <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        
        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {products && products.length > 0 && products.slice(0, 8).map((item) => (
             <div key={item.id} className="bg-[#0f172a] border border-slate-800 hover:border-[#22d3ee]/50 transition-colors duration-300">
                <ProductCard item={item} />
             </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default TemplateStore;